import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ResumoGuiasOutrasOps } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  AgFilterUtils,
  LongColumnHintComponent,
  buildDefaultColumnDef,
  buildGridOptions,
  exportGridAsExcel,
} from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { ResumoGuiasOutrasOpsService } from './resumo-guias-outras-ops.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.RESUMO_GUIAS_OUTRAS_OPERADORAS';

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    minWidth: 100,
    type: 'competencia',
  },
  {
    headerName: '014-CNPJ/CPF executante',
    field: 'cnpj_prestador',
    width: 150,
  },
  {
    headerName: 'Regans do Prest. Executante (OPS)',
    field: 'regans_ops_prestador',
    width: 170,
  },
  {
    headerName: 'Nome do Prest. Executante (OPS)',
    field: 'nome_ops_prestador',
    sort: 'asc',
    width: 190,
  },
  {
    headerName: '081-Registro ANS da OPS intermediária',
    field: 'regans_ops_intermediaria',
    width: 190,
  },
  {
    headerName: 'Nome OPS intermediária',
    field: 'nome_ops_intermediaria',
    sort: 'asc',
    width: 180,
  },
  {
    headerName: 'CNES da OPS Intermediária',
    field: 'cnes_matriz_ops',
    width: 150,
  },
  {
    headerName: 'Tipo de rede',
    field: 'nm_tipo_rede',
    width: 120,
  },
  {
    headerName: 'Qtde de guia',
    field: 'qtde_guia',
    type: 'int',
    width: 120,
  },
  {
    headerName: '050-Valor informado da guia',
    field: 'vl_informado',
    type: 'money',
    width: 180,
  },
  {
    headerName: '012-CNES Executante',
    field: 'lista_cnes_guia',
    maxWidth: 160,
  },
  {
    headerName: 'Amostra Nº guia da operadora',
    field: 'amostra_nr_guia_operadora',
    width: 170,
    cellRenderer: LongColumnHintComponent,
    //maxWidth: 300,
  },
  {
    headerName: 'Cód. tipo estabelecimento',
    field: 'cd_tipo_estabelecimento_prestador',
    hide: true,
  },
  {
    headerName: 'Desc. tipo estabelecimento',
    field: 'ds_tipo_estabelecimento_prestador',
    hide: true,
  },
  {
    headerName: 'Verificação',
    field: 'ds_verificacao',
    width: 120,
  },
];

@Component({
    templateUrl: './resumo-guias-outras-ops.component.html',
    styleUrls: ['./resumo-guias-outras-ops.component.scss'],
    providers: [ResumoGuiasOutrasOpsService, DecimalPipe],
    standalone: false
})
export class ResumoGuiasOutrasOpsComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowData: Row[];

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: ResumoGuiasOutrasOpsService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        defaultColDef: buildDefaultColumnDef({
          cellClassRules: {
            'cell-red': params => {
              if (!params.api.isPivotMode()) return params.data?.ds_verificacao?.charAt(0) == '1';
              if (params.node.field === 'ds_verificacao') return params.node.key?.charAt(0) == '1';
              return params.node.allLeafChildren?.some(n => n.data?.ds_verificacao?.charAt(0) == '1') ?? false;
            },
            'cell-yellow': params => {
              if (!params.api.isPivotMode()) return params.data?.ds_verificacao?.charAt(0) == '2';
              if (params.node.field === 'ds_verificacao') return params.node.key?.charAt(0) == '2';
              const leaves = params.node.allLeafChildren;
              if (!leaves) return false;
              return !leaves.some(n => n.data?.ds_verificacao?.charAt(0) == '1') &&
                      leaves.some(n => n.data?.ds_verificacao?.charAt(0) == '2');
            },
          },
        }),
        excelStyles: [
          { id: 'cell-yellow', interior: { color: '#ffe699', pattern: 'Solid' } },
          { id: 'cell-red', interior: { color: '#ff8181', pattern: 'Solid' } },
        ],
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]).subscribe({
      next: items => {
        if (!items?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        this.rowData = items.map(item => {
          const row = {
            ...item,
            nm_tipo_rede: '',
          };

          row.nm_tipo_rede = this.geralService.buildNomeDeDominio(row.tipo_rede, 'MON_TIPO_REDE');

          return row;
        });

        const totalData = items.reduce(
          (acc, item) => ({
            nr_competencia: null,
            cnpj_prestador: null,
            regans_ops_prestador: null,
            nome_ops_prestador: null,
            regans_ops_intermediaria: null,
            nome_ops_intermediaria: null,
            cnes_matriz_ops: null,
            tipo_rede: null,
            qtde_guia: acc.qtde_guia + item.qtde_guia,
            vl_informado: acc.vl_informado + item.vl_informado,
            lista_cnes_guia: null,
            amostra_nr_guia_operadora: null,
            ds_verificacao: null,
            id_verificacao: 0,
          }),
          {
            qtde_guia: 0,
            vl_informado: 0,
          },
        );

        this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    // this.reloadData();
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `Resumo-Guias-Outras-OPS-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Resumo guias de outras OPS',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends ResumoGuiasOutrasOps {
  nm_tipo_rede: string;
}
